
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationProvider, extend } from 'vee-validate';
import StyledGoogleAutocomplete from '@/components/shared/StyledGoogleAutocomplete.vue';
import TextField from '@/components/shared/TextField.vue';
import '@/validation-rules';

@Component<DeliveryAddress>({
	components: {
		TextField,
		StyledGoogleAutocomplete,
		ValidationProvider
	}
})
export default class DeliveryAddress extends Vue {
	@Action('setCosts', { namespace: 'orders' }) private setCosts!: () => Promise<void>;
	@Getter('getDeliveryCharge', { namespace: 'orders' }) private deliveryCharge!: string | null;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('getCountry', { namespace: 'auth' }) private countryCode!: Restaurant;
	@Prop({ type: Object, required: false, default: null }) private restaurantDeliveryInfo!: AddressDelivery | null;
	@Prop({ type: Object, required: true }) private deliveryInformation!: DeliveryInfo;
	@Prop({ type: String, required: false, default: '' }) private coordinatesErrorMsg!: string;
	@Prop({ type: String, required: false, default: '' }) private label!: string;
	@Prop({ type: Boolean, required: false, default: false }) private hideFeePreview!: boolean;

	$refs!: {
		addressValidation: any;
	};

	private delivery: DeliveryInfo = {
		type: 'address',
		address: '',
		extra: ''
	};
	private addressString: string = '';
	private autocompleteLocationSelected: boolean = false;
	private calculatingFee: boolean = false;

	/**
	 * Check if the restaurant uses custom delivery fee ranges (distance-based pricing)
	 *
	 * @return {boolean}
	 */
	private get hasCustomFeeRanges(): boolean {
		return this.restaurantDeliveryInfo?.delivery_fee_type === 'custom' &&
			!!this.restaurantDeliveryInfo.delivery_fee_ranges?.length;
	}

	/**
	 * Show the delivery fee preview after an address has been selected
	 * and the fee has been calculated by the backend via setCosts
	 *
	 * @return {string}
	 */
	private get feePreview(): string {
		if (!this.autocompleteLocationSelected || !this.deliveryCharge || this.calculatingFee) {
			return '';
		}
		return this.$n(+this.deliveryCharge, 'currency');
	}

	/**
	 * Add custom validation rule for address selection
	 *
	 * @return {void}
	 */
	private created(): void {
		extend('address-selected', {
			validate: () => {
				return this.autocompleteLocationSelected;
			},
			message: this.$t('orders.guest_details.delivery.error_select_address')
		});
	}

	/**
	 * Set the temporary delivery information to the
	 * vuex store values if there are any to prevent
	 * refilling the same information again, also verifying
	 * if there are lat/lng to make sure that we can compare
	 * the distance.
	 *
	 * @return {void}
	 */
	private mounted(): void {
		if (this.deliveryInformation) {
			const splitAddress = this.deliveryInformation.address?.split('. Extra (Apt,Suite,Etc.): ');
			if (splitAddress?.length) {
				this.addressString = splitAddress[0];
				const address = splitAddress[0];
				const extra = splitAddress[1] || '';
				this.delivery = {
					...this.deliveryInformation,
					address,
					extra
				};
			}
			else {
				this.delivery = this.deliveryInformation;
			}
			this.delivery.type = 'address';

			if(this.delivery.lat && this.delivery.lng) {
				this.autocompleteLocationSelected = true;
			}
		}
	}

	/**
	 * Validate if an address has been selected from the autocomplete
	 * If selected send an event to the parent to update the delivery information in the vuex store
	 *
	 * @return {void}
	 */
	private updateDeliveryInfo(): void {
		this.$refs.addressValidation.validate();
		this.$emit('delivery-info-updated', this.delivery);
	}

	/**
	 * Deconstruct the address object returned by the autocomplete.
	 *
	 * @param {any} addressData - Address data returned by google autocomplete
	 * @param {any} placeResultData - Place data returned by google autocomplete
	 * @return {Promise<void>}
	 */
	private async autofillAddress(addressData: any, placeResultData: any): Promise<void> {
		this.autocompleteLocationSelected = true;
		this.delivery.address = placeResultData.formatted_address;
		this.delivery.country = addressData.country;
		this.delivery.lat = addressData.latitude;
		this.delivery.lng = addressData.longitude;
		this.delivery.city = addressData.locality;
		this.delivery.province = this.filterProvince(addressData.administrative_area_level_1);
		this.delivery.postalCode = addressData.postal_code;
		this.delivery.streetName = addressData.route;
		this.delivery.streetNumber = addressData.street_number;
		this.delivery.radius = this.restaurantDeliveryInfo?.radius;
		this.updateDeliveryInfo();

		if (this.hasCustomFeeRanges) {
			this.calculatingFee = true;
			await this.setCosts();
			this.calculatingFee = false;
		}
	}

	/**
	 * Check if the user has modified the address input and set autocompleteLocationSelected accordingly
	 * This is to make sure that the user has selected an address from the autocomplete
	 * which ensure we have lat/lng for the distance validation
	 *
	 * @return {void}
	 */
	private handleKeyup(): void {
		if (this.addressString !== this.delivery.address) {
			this.autocompleteLocationSelected = false;
		}
		else {
			this.autocompleteLocationSelected = true;
		}
	}

	/**
	 * On blur check if the address has been filled by the autocomplete
	 *
	 * @return {void}
	 */
	private handleBlur(): void {
		this.$refs.addressValidation.validate();
	}

	/**
	 * Filter provinces from short_name to long_name because
	 * autocomplete return the long_name only in the array object
	 * without variables so it's more optimal to have a switch case
	 * with the short_name than to loop through the 8 values returned
	 *
	 * @param {string} provinceShortName
	 * @return {string}
	 */
	private filterProvince(provinceShortName: string): string {
		switch (provinceShortName.toLowerCase()) {
			case 'ab':
				return this.$t('shared.province_filters.ab');
			case 'bc':
				return this.$t('shared.province_filters.bc');
			case 'mb':
				return this.$t('shared.province_filters.mb');
			case 'nb':
				return this.$t('shared.province_filters.nb');
			case 'nl':
				return this.$t('shared.province_filters.nl');
			case 'ns':
				return this.$t('shared.province_filters.ns');
			case 'nt':
				return this.$t('shared.province_filters.nt');
			case 'on':
				return this.$t('shared.province_filters.on');
			case 'pe':
				return this.$t('shared.province_filters.pe');
			case 'qc':
				return this.$t('shared.province_filters.qc');
			case 'sk':
				return this.$t('shared.province_filters.sk');
			case 'yt':
				return this.$t('shared.province_filters.yt');
			default:
				return '';
		}
	}
}
